import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Copy, Download, Sparkles, Users, Briefcase, Heart, Gamepad2, Camera } from "lucide-react";
import { useEffect, useState } from "react";
import { useToast } from "@/hooks/use-toast";

const Resources = () => {
  const { toast } = useToast();
  const [copiedText, setCopiedText] = useState<string | null>(null);

  useEffect(() => {
    document.title = "Free Resources: Username Examples & Bio Templates | NameBioLab";
    
    const structuredData = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://namebiolab.lovable.app/resources#webpage",
          "url": "https://namebiolab.lovable.app/resources",
          "name": "Free Resources: Username Examples & Bio Templates",
          "description": "Access hundreds of free username examples, bio templates, and downloadable guides for Instagram, TikTok, YouTube, and all social media platforms.",
          "isPartOf": {
            "@id": "https://namebiolab.lovable.app/#webapp"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://namebiolab.lovable.app/resources#breadcrumb",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://namebiolab.lovable.app/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Resources",
              "item": "https://namebiolab.lovable.app/resources"
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
    setTimeout(() => setCopiedText(null), 2000);
  };

  const usernameExamples = [
    {
      category: "Creative & Artistic",
      icon: Camera,
      color: "text-purple-500",
      examples: [
        "PixelDreamer", "ArtfulMuse", "CreativeCanvas", "VisualPoetry",
        "SketchyVibes", "ColorPalettePro", "InkAndImagination", "DesignDose",
        "AbstractMinds", "BrushStrokeStudio", "CreativeChaos", "ArtisticSoul"
      ]
    },
    {
      category: "Gaming & Tech",
      icon: Gamepad2,
      color: "text-blue-500",
      examples: [
        "CyberNinja", "PixelWarrior", "TechSavvyGamer", "CodeCrusader",
        "DigitalNomad", "ByteBandit", "NeonGlitch", "VirtualVanguard",
        "QuantumQuest", "DataDynamo", "TechTactician", "GameMasterPro"
      ]
    },
    {
      category: "Business & Professional",
      icon: Briefcase,
      color: "text-green-500",
      examples: [
        "BrandBuilderJoe", "MarketingMaven", "StartupSavvy", "BizInnovator",
        "ProfessionalEdge", "LeadershipLab", "SuccessStrategist", "EntrepreneurElite",
        "GrowthGuru", "BusinessBrilliance", "CorporateClimber", "ExecutiveExcellence"
      ]
    },
    {
      category: "Lifestyle & Wellness",
      icon: Heart,
      color: "text-pink-500",
      examples: [
        "WellnessWarrior", "MindfulMoments", "HealthyHabitsDaily", "YogaVibes",
        "FitnessFirst", "BalancedLifestyle", "SereneSoul", "VitalityVixen",
        "HolisticHealing", "WellnessWhisperer", "ZenZone", "PurelyPositive"
      ]
    },
    {
      category: "Social & Influencer",
      icon: Users,
      color: "text-orange-500",
      examples: [
        "TrendingNow", "ViralVibes", "SocialSavvy", "InfluencerLife",
        "ContentCreatorCo", "TheRealDeal", "AuthenticVibes", "TrendSetter",
        "CommunityBuilder", "EngagementExpert", "SocialSphere", "DigitalInfluencer"
      ]
    },
    {
      category: "Aesthetic & Minimal",
      icon: Sparkles,
      color: "text-yellow-500",
      examples: [
        "MoonlitDreams", "GoldenHourGlow", "CloudNine", "VelvetVibes",
        "CosmicAura", "SereneScenes", "MinimalMagic", "PurelyAesthetic",
        "EtherealEssence", "SoftAesthetics", "DreamyDaze", "CelestialVibes"
      ]
    }
  ];

  const bioTemplates = [
    {
      platform: "Instagram",
      description: "Perfect for visual creators and lifestyle brands",
      templates: [
        {
          title: "Lifestyle Influencer",
          template: "✨ Living life one adventure at a time\n📍 [Your City]\n💌 Collab: [email]\n👇 Shop my favs"
        },
        {
          title: "Business/Coach",
          template: "🎯 Helping [target audience] achieve [goal]\n📊 [X]+ success stories\n🎓 Certified [credential]\n👇 Free guide below"
        },
        {
          title: "Creative Professional",
          template: "🎨 [Your profession] creating [what you create]\n💼 Featured in [publications]\n🌍 Based in [location]\n📧 Work with me 👇"
        }
      ]
    },
    {
      platform: "TikTok",
      description: "Short, punchy bios for video creators",
      templates: [
        {
          title: "Content Creator",
          template: "just trying to make u laugh 😂\n[niche] content daily\nfollow for more ✨"
        },
        {
          title: "Educational",
          template: "teaching you [topic] 📚\n[X]M+ taught & counting\nNew tips every [day]"
        },
        {
          title: "Entertainment",
          template: "[personality trait] + [niche]\nposting [frequency]\nmainly here for the comments"
        }
      ]
    },
    {
      platform: "LinkedIn",
      description: "Professional bios for career-focused networking",
      templates: [
        {
          title: "Executive/Leader",
          template: "[Title] at [Company] | [Industry] Expert | Helping [target] achieve [outcome]\n\n[X]+ years driving [results]. Speaker, mentor, and [credential].\n\nLet's connect!"
        },
        {
          title: "Freelancer/Consultant",
          template: "Freelance [profession] specializing in [niche]\n\n✅ [Service 1]\n✅ [Service 2]\n✅ [Service 3]\n\nWorked with [notable clients/brands]. Open for projects."
        },
        {
          title: "Job Seeker",
          template: "[Current role] seeking opportunities in [field]\n\nSkills: [skill 1] | [skill 2] | [skill 3]\n[Degree] from [University]\n\nPassionate about [industry/cause]"
        }
      ]
    },
    {
      platform: "Twitter/X",
      description: "Concise bios with personality",
      templates: [
        {
          title: "Professional",
          template: "[Job title] @[Company]. Thoughts on [topics]. RT ≠ endorsement. Views my own."
        },
        {
          title: "Creator",
          template: "making [content type] about [niche] • [X]K on [platform] • subscribe: [link]"
        },
        {
          title: "Thought Leader",
          template: "[Expertise] | Writing about [topics] | [Credential] | Newsletter: [link]"
        }
      ]
    },
    {
      platform: "YouTube",
      description: "Channel descriptions that convert subscribers",
      templates: [
        {
          title: "Educational Channel",
          template: "Welcome to [Channel Name]! 🎓\n\nI help [target audience] [achieve goal] through [method].\n\nNew videos every [schedule]\nSubscribe for [value promise]\n\n[X]K+ students learning [topic]"
        },
        {
          title: "Entertainment/Vlog",
          template: "Hey! I'm [Name] 👋\n\n[What you do] • [Personality trait]\n\nJoin me as I [content description]\n\nPosting [frequency]\nBusiness: [email]"
        },
        {
          title: "Tutorial/How-To",
          template: "[Topic] Tutorials & Tips\n\n✅ Step-by-step guides\n✅ Beginner-friendly\n✅ [Frequency] uploads\n\n[X]+ tutorials | Subscribe & level up!"
        }
      ]
    }
  ];

  const downloadableGuides = [
    {
      title: "Ultimate Username Checklist",
      description: "A comprehensive checklist to evaluate username ideas before committing",
      items: ["Platform availability", "Pronunciation test", "Spelling difficulty", "Brand alignment", "Long-term viability"]
    },
    {
      title: "Bio Writing Framework",
      description: "Step-by-step framework for crafting compelling bios on any platform",
      items: ["Identify hook", "Define value prop", "Add credibility", "Include CTA", "Optimize for keywords"]
    },
    {
      title: "Social Media Branding Kit",
      description: "Essential elements for consistent branding across all platforms",
      items: ["Color palette guide", "Font pairing tips", "Bio formula", "Content pillars", "Posting schedule"]
    },
    {
      title: "Platform Character Limits",
      description: "Quick reference for bio character limits across all major platforms",
      items: ["Instagram: 150", "Twitter/X: 160", "TikTok: 80", "LinkedIn: 220", "YouTube: 1000"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <Link to="/">
          <Button variant="ghost" className="mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>

        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Free Resources & Templates</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Everything you need to create the perfect username and bio. Copy our templates, 
            get inspired by examples, and download our free guides.
          </p>
        </header>

        {/* Username Examples Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3">Username Examples by Category</h2>
            <p className="text-muted-foreground">
              Browse hundreds of creative username ideas organized by niche. Click to copy instantly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {usernameExamples.map((category) => {
              const IconComponent = category.icon;
              return (
                <Card key={category.category} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <IconComponent className={`h-6 w-6 ${category.color}`} />
                      <CardTitle className="text-xl">{category.category}</CardTitle>
                    </div>
                    <CardDescription className="text-sm">
                      {category.examples.length} creative username ideas
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-2">
                      {category.examples.map((username) => (
                        <button
                          key={username}
                          onClick={() => copyToClipboard(username, username)}
                          className="text-sm px-3 py-2 bg-muted hover:bg-muted/80 rounded-md text-left transition-colors flex items-center justify-between group"
                        >
                          <span className="truncate">{username}</span>
                          <Copy className={`h-3 w-3 ml-2 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${
                            copiedText === username ? 'text-green-500' : ''
                          }`} />
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">Need a unique username tailored to you?</p>
            <Link to="/">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary">
                <Sparkles className="mr-2 h-5 w-5" />
                Generate Custom Username
              </Button>
            </Link>
          </div>
        </section>

        {/* Bio Templates Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3">Bio Templates by Platform</h2>
            <p className="text-muted-foreground">
              Copy and customize these proven bio templates for any social media platform.
            </p>
          </div>

          <div className="space-y-6">
            {bioTemplates.map((platform) => (
              <Card key={platform.platform}>
                <CardHeader>
                  <CardTitle className="text-2xl">{platform.platform}</CardTitle>
                  <CardDescription>{platform.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-3 gap-4">
                    {platform.templates.map((template) => (
                      <div key={template.title} className="bg-muted rounded-lg p-4">
                        <h4 className="font-semibold mb-3 text-sm">{template.title}</h4>
                        <div className="bg-background rounded p-3 mb-3 whitespace-pre-wrap text-sm font-mono">
                          {template.template}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(template.template, `${platform.platform} - ${template.title}`)}
                          className="w-full"
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          {copiedText === `${platform.platform} - ${template.title}` ? 'Copied!' : 'Copy Template'}
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground mb-4">Want a personalized bio written just for you?</p>
            <Link to="/">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary">
                <Sparkles className="mr-2 h-5 w-5" />
                Generate Custom Bio
              </Button>
            </Link>
          </div>
        </section>

        {/* Downloadable Guides Section */}
        <section className="mb-16">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold mb-3">Quick Reference Guides</h2>
            <p className="text-muted-foreground">
              Essential checklists and frameworks to create standout usernames and bios.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {downloadableGuides.map((guide) => (
              <Card key={guide.title} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg mb-2">{guide.title}</CardTitle>
                      <CardDescription>{guide.description}</CardDescription>
                    </div>
                    <Download className="h-5 w-5 text-muted-foreground flex-shrink-0 ml-3" />
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {guide.items.map((item) => (
                      <li key={item} className="text-sm flex items-start">
                        <span className="text-primary mr-2">✓</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Blog CTA Section */}
        <section className="mb-16 bg-card rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Want More Tips & Strategies?</h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            Check out our blog for in-depth guides on username creation, bio writing, and social media branding.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/blog">
              <Button size="lg" variant="outline">
                Read Our Blog
              </Button>
            </Link>
            <Link to="/">
              <Button size="lg" className="bg-gradient-to-r from-primary to-secondary">
                Try AI Generator
              </Button>
            </Link>
          </div>
        </section>

        {/* Tips Section */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold mb-6 text-center">Pro Tips for Using These Resources</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Customizing Username Examples</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>• Replace generic words with your niche-specific terms</p>
                <p>• Add numbers or your name for uniqueness (e.g., PixelDreamer → PixelDreamer23)</p>
                <p>• Combine ideas from different categories</p>
                <p>• Check availability across all platforms before committing</p>
                <p>• Test pronunciation - if people can't say it, they won't remember it</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Adapting Bio Templates</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>• Replace [bracketed text] with your specific information</p>
                <p>• Adjust emojis to match your brand personality</p>
                <p>• Keep it concise - every word should add value</p>
                <p>• Include a clear call-to-action (link, follow, DM, etc.)</p>
                <p>• Update regularly with new achievements or offerings</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Testing Your Choices</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>• Ask friends for honest feedback on usernames</p>
                <p>• A/B test different bio variations to see what converts better</p>
                <p>• Google your username to check for existing associations</p>
                <p>• Read your bio out loud to check flow and clarity</p>
                <p>• Verify it looks good on mobile (where most people view it)</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Maintaining Consistency</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <p>• Use the same username across all platforms when possible</p>
                <p>• Keep your bio style consistent with your brand voice</p>
                <p>• Update bios simultaneously across platforms</p>
                <p>• Maintain the same profile picture everywhere</p>
                <p>• Coordinate your username, bio, and content theme</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Related Resources */}
        <section>
          <h2 className="text-3xl font-bold mb-6 text-center">Related Resources</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/blog/creative-username-ideas-2025">
              <Card className="hover:shadow-lg transition-shadow h-full cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">500+ Username Ideas</CardTitle>
                  <CardDescription>
                    Complete guide with hundreds of creative username examples and formulas
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/blog/perfect-bio-writing-tips">
              <Card className="hover:shadow-lg transition-shadow h-full cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">Bio Writing Guide</CardTitle>
                  <CardDescription>
                    15 proven tips for writing bios that convert followers and build credibility
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>

            <Link to="/blog/social-media-branding-guide">
              <Card className="hover:shadow-lg transition-shadow h-full cursor-pointer">
                <CardHeader>
                  <CardTitle className="text-lg">Branding Guide</CardTitle>
                  <CardDescription>
                    Complete guide to building a cohesive brand across all social platforms
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Resources;
